import { BigNumber } from '@ethersproject/bignumber'
import { TransactionResponse } from '@ethersproject/providers'
import { useCallback, useMemo } from 'react'

import { TransactionType } from '../state/transactions/actions'
import { useHasPendingTwapPeriod, useTransactionAdder } from '../state/transactions/hooks'
import { calculateGasMargin } from '../utils/calculateGasMargin'
import { useVaultContract } from './useContract'
import { useActiveWeb3React } from './web3'

export enum TwapPeriodState {
  IDLE = 'IDLE',
  PENDING = 'PENDING',
}

// returns a variable indicating the state of the twapPeriod setting and a function which sets twapPeriod if necessary or early returns
export function useTwapPeriodCallback(vaultAddress?: string, twapPeriodInput?: string): [() => Promise<void>] {
  const { chainId } = useActiveWeb3React()
  const pendingTwapPeriod = useHasPendingTwapPeriod(vaultAddress)

  const twapPeriodState: TwapPeriodState = useMemo(() => {
    return pendingTwapPeriod ? TwapPeriodState.PENDING : TwapPeriodState.IDLE
  }, [pendingTwapPeriod])

  const vaultContract = useVaultContract(vaultAddress)
  const addTransaction = useTransactionAdder()

  const twapPeriod = useCallback(async (): Promise<void> => {
    if (twapPeriodState !== TwapPeriodState.IDLE) {
      console.error('TwapPeriod was called unnecessarily')
      return
    }
    if (!chainId) {
      console.error('no chainId')
      return
    }
    if (!vaultAddress) {
      console.error('no vault')
      return
    }
    if (!vaultContract) {
      console.error('vaultContract is null')
      return
    }
    if (!twapPeriodInput) {
      console.error('missing twap period value')
      return
    }

    return vaultContract
      .setTwapPeriod(twapPeriodInput.toString(), {
        gasLimit: calculateGasMargin(BigNumber.from('160000')),
      })
      .then((response: TransactionResponse) => {
        addTransaction(response, {
          type: TransactionType.TWAP_PERIOD,
          vaultAddress,
          twapPeriodValue: twapPeriodInput,
        })
      })
      .catch((error: Error) => {
        console.debug('Failed to set twap period: ', error)
        throw error
      })
  }, [twapPeriodState, twapPeriodInput, chainId, vaultAddress, vaultContract, addTransaction])

  return [twapPeriod]
}
